/**
 * JayChris EDI — Live Demo Component
 *
 * Accessible at /demo route:
 *   - When logged in as admin (user.role === 'admin')
 *   - When URL contains ?preview=true (unauthenticated sales demo)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

// ─── Constants ────────────────────────────────────────────────────────────────

const RETAILERS = [
  { value: 'walmart', label: 'Walmart' },
  { value: 'target', label: 'Target' },
  { value: 'kohls', label: "Kohl's" },
];

const RETAILER_CONFIGS = {
  walmart: { name: 'Walmart', ediId: '0069268510', poPrefix: '4501', shipTo: { name: 'Walmart DC #6094', city: 'Milton', state: 'FL', zip: '32583' } },
  target:  { name: 'Target',  ediId: '0000002037', poPrefix: '3301', shipTo: { name: 'Target DC #0522',  city: 'Middleton', state: 'WI', zip: '53562' } },
  kohls:   { name: "Kohl's",  ediId: '0000001928', poPrefix: '7201', shipTo: { name: "Kohl's DC #1072", city: 'Menomonee Falls', state: 'WI', zip: '53051' } },
};

const LINE_ITEMS = [
  { lineNum: 1, sku: 'JCE-WMS-001', upc: '012345678901', description: 'Wireless Mouse - Black',         qty: 24, unitPrice: 18.5 },
  { lineNum: 2, sku: 'JCE-KBD-002', upc: '012345678902', description: 'Mechanical Keyboard - Silver',    qty: 12, unitPrice: 67.0 },
  { lineNum: 3, sku: 'JCE-MON-003', upc: '012345678903', description: '27" Monitor Stand - Matte Black', qty: 8,  unitPrice: 42.0 },
  { lineNum: 4, sku: 'JCE-USB-004', upc: '012345678904', description: 'USB-C Hub 7-Port',                qty: 36, unitPrice: 29.0 },
];

function rand(n) { return Math.floor(Math.random() * n); }
function randId(len) { return Math.random().toString(36).substring(2, 2 + len).toUpperCase(); }

function buildDemoEvents(retailerKey) {
  const cfg = RETAILER_CONFIGS[retailerKey] || RETAILER_CONFIGS.walmart;
  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];
  const shipDate     = fmt(new Date(today.getTime() + 5  * 86400000));
  const deliveryDate = fmt(new Date(today.getTime() + 10 * 86400000));
  const poNumber     = cfg.poPrefix + (1000000 + rand(9000000));
  const orderId      = 'ORD-' + randId(8);
  const invoiceNum   = 'INV-' + (10000000 + rand(90000000));
  const tracking     = '1Z' + randId(13);
  const ssccBase     = '000123456789' + String(rand(100000000)).padStart(8, '0');

  const items    = LINE_ITEMS.map(i => ({ ...i, unit: 'EA' }));
  const total    = items.reduce((s, i) => s + i.qty * i.unitPrice, 0);
  const po       = { poNumber, retailer: cfg.name, ediId: cfg.ediId, items, total, shipTo: cfg.shipTo, shipDate, deliveryDate, currency: 'USD' };
  const asn      = { poNumber, trackingNumber: tracking, carrier: 'UPS', carrierCode: 'UPSS', shipDate, cartons: [
    { cartonId: 1, sscc: ssccBase + '0', weight: '14.2 lbs', dimensions: '18x14x12 in', items: [{ sku: items[0].sku, qty: 12 }, { sku: items[1].sku, qty: 6 }] },
    { cartonId: 2, sscc: ssccBase + '1', weight: '11.8 lbs', dimensions: '16x12x10 in', items: [{ sku: items[2].sku, qty: 4 }, { sku: items[3].sku, qty: 18 }] },
  ]};
  const invoice  = { invoiceNumber: invoiceNum, poNumber, invoiceDate: fmt(today), shipDate, trackingNumber: tracking,
    lineItems: items.map(i => ({ ...i, total: (i.qty * i.unitPrice).toFixed(2) })),
    subtotal: total.toFixed(2), tax: '0.00', total: total.toFixed(2), paymentTerms: 'Net 30' };

  return [
    { step: 1, status: 'processing', title: `${cfg.name} PO #${poNumber} Received`,         description: `${items.length} line items · $${total.toFixed(2)} · Ship by ${shipDate}`, document: po,      timing: 0 },
    { step: 1, status: 'complete',   title: `${cfg.name} PO #${poNumber} Received`,         description: `${items.length} line items · $${total.toFixed(2)} · Ship by ${shipDate}`, document: po,      timing: 2000 },
    { step: 2, status: 'processing', title: 'PO Acknowledged — EDI 855 Transmitted',        description: `Confirmed receipt of PO #${poNumber} to ${cfg.name}`,                     document: { acknowledgedPO: poNumber, status: 'Accepted', timestamp: today.toISOString(), retailer: cfg.name, ediId: cfg.ediId }, timing: 2500 },
    { step: 2, status: 'complete',   title: 'PO Acknowledged — EDI 855 Transmitted',        description: `Confirmed receipt of PO #${poNumber} to ${cfg.name}`,                     document: { acknowledgedPO: poNumber, status: 'Accepted', timestamp: today.toISOString(), retailer: cfg.name, ediId: cfg.ediId }, timing: 4500 },
    { step: 3, status: 'processing', title: 'Order Created in System',                      description: `Order ${poNumber} created · Assigned to fulfillment queue`,               document: { orderId, poNumber, status: 'Processing', lineItems: items, shipTo: cfg.shipTo, shipDate, deliveryDate }, timing: 5000 },
    { step: 3, status: 'complete',   title: 'Order Created in System',                      description: `Order ${poNumber} created · Assigned to fulfillment queue`,               document: { orderId, poNumber, status: 'Processing', lineItems: items, shipTo: cfg.shipTo, shipDate, deliveryDate }, timing: 8000 },
    { step: 4, status: 'processing', title: 'Shipment Processed — EDI 856 ASN Transmitted', description: `2 cartons · Tracking: ${tracking} · Carrier: UPS`,                        document: asn,     timing: 8500 },
    { step: 4, status: 'complete',   title: 'Shipment Processed — EDI 856 ASN Transmitted', description: `2 cartons · Tracking: ${tracking} · Carrier: UPS`,                        document: asn,     timing: 10500 },
    { step: 5, status: 'processing', title: 'Invoice Generated — EDI 810 Transmitted',      description: `Invoice #${invoiceNum} · $${total.toFixed(2)} · Payment terms: Net 30`,   document: invoice, timing: 11000 },
    { step: 5, status: 'complete',   title: 'Invoice Generated — EDI 810 Transmitted',      description: `Invoice #${invoiceNum} · $${total.toFixed(2)} · Payment terms: Net 30`,   document: invoice, timing: 13000 },
    { step: 0, status: 'done',       title: 'Full EDI Cycle Complete',                      description: 'Zero manual intervention required',
      document: { summary: { poNumber, retailer: cfg.name, orderId, trackingNumber: tracking, invoiceNumber: invoiceNum, total: total.toFixed(2), stepsCompleted: 5, documentsExchanged: ['EDI 850','EDI 855','EDI 856','EDI 810'] } },
      totalTime: 14000, timing: 14000 },
  ];
}

const STEP_META = [
  {
    num: 1,
    label: 'EDI 850',
    subtitle: 'Purchase Order Received',
    color: '#3b82f6',
  },
  {
    num: 2,
    label: 'EDI 855',
    subtitle: 'PO Acknowledgment Sent',
    color: '#8b5cf6',
  },
  {
    num: 3,
    label: 'Order',
    subtitle: 'Created in System',
    color: '#f59e0b',
  },
  {
    num: 4,
    label: 'EDI 856',
    subtitle: 'ASN Transmitted',
    color: '#10b981',
  },
  {
    num: 5,
    label: 'EDI 810',
    subtitle: 'Invoice Transmitted',
    color: '#ef4444',
  },
];

// ─── Styles (inline — matches existing app dark/navy palette) ─────────────────

const S = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    color: 'white',
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  header: {
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    borderBottom: '1px solid #334155',
    padding: '20px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px',
  },
  logo: {
    fontSize: '22px',
    fontWeight: '800',
    color: '#f97316',
    letterSpacing: '-0.5px',
  },
  logoSub: {
    fontSize: '13px',
    color: '#94a3b8',
    marginLeft: '10px',
    fontWeight: '400',
  },
  timerBox: {
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '10px',
    padding: '8px 20px',
    fontSize: '22px',
    fontWeight: '700',
    color: '#10b981',
    fontFamily: 'monospace',
    minWidth: '90px',
    textAlign: 'center',
  },
  main: {
    maxWidth: '960px',
    margin: '0 auto',
    padding: '40px 24px 80px',
  },
  controlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '48px',
    flexWrap: 'wrap',
  },
  select: {
    background: '#1e293b',
    border: '1px solid #475569',
    borderRadius: '10px',
    color: 'white',
    padding: '12px 16px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    minWidth: '160px',
  },
  runBtn: {
    background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    border: 'none',
    borderRadius: '10px',
    color: 'white',
    padding: '12px 32px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    letterSpacing: '0.3px',
    boxShadow: '0 4px 14px rgba(249,115,22,0.4)',
    transition: 'opacity 0.15s',
  },
  runBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  secondaryBtn: {
    background: 'transparent',
    border: '1px solid #475569',
    borderRadius: '10px',
    color: '#94a3b8',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'border-color 0.15s, color 0.15s',
  },
  copyBtn: {
    background: 'transparent',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#64748b',
    padding: '10px 16px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  stepsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  stepCard: (status, color) => ({
    background:
      status === 'pending'
        ? '#1e293b'
        : status === 'active'
        ? '#1e293b'
        : '#1a2e1a',
    border:
      status === 'pending'
        ? '1px solid #334155'
        : status === 'active'
        ? `1px solid ${color}`
        : `1px solid #16a34a`,
    borderRadius: '14px',
    overflow: 'hidden',
    transition: 'border-color 0.3s, background 0.3s',
    boxShadow:
      status === 'active' ? `0 0 0 2px ${color}33` : 'none',
  }),
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px 24px',
    cursor: 'pointer',
  },
  circle: (status, color) => ({
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '800',
    background:
      status === 'pending'
        ? '#334155'
        : status === 'active'
        ? color
        : '#16a34a',
    color: status === 'pending' ? '#64748b' : 'white',
    transition: 'background 0.3s',
    animation: status === 'active' ? 'pulse-ring 1.5s infinite' : 'none',
  }),
  stepMeta: {
    flex: 1,
  },
  stepLabel: (status) => ({
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: status === 'pending' ? '#475569' : '#94a3b8',
    marginBottom: '3px',
  }),
  stepTitle: (status) => ({
    fontSize: '16px',
    fontWeight: '700',
    color: status === 'pending' ? '#475569' : 'white',
    marginBottom: '4px',
  }),
  stepDesc: (status) => ({
    fontSize: '13px',
    color: status === 'pending' ? '#334155' : '#94a3b8',
  }),
  stepTimestamp: {
    fontSize: '12px',
    color: '#10b981',
    fontFamily: 'monospace',
    flexShrink: 0,
  },
  docPanel: {
    borderTop: '1px solid #1e3a5f',
    background: '#0f172a',
    padding: '20px 24px',
  },
  docToggle: {
    background: 'none',
    border: 'none',
    color: '#3b82f6',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    color: '#64748b',
    fontWeight: '700',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #1e293b',
  },
  td: {
    padding: '10px 12px',
    color: '#cbd5e1',
    borderBottom: '1px solid #0f172a',
    fontFamily: 'monospace',
  },
  summaryCard: {
    background: 'linear-gradient(135deg, #064e3b 0%, #065f46 100%)',
    border: '1px solid #10b981',
    borderRadius: '16px',
    padding: '32px',
    textAlign: 'center',
    marginTop: '32px',
    boxShadow: '0 0 40px rgba(16,185,129,0.2)',
  },
  ctaBtn: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    color: 'white',
    padding: '14px 36px',
    borderRadius: '10px',
    fontWeight: '700',
    fontSize: '16px',
    textDecoration: 'none',
    marginTop: '24px',
    boxShadow: '0 4px 14px rgba(249,115,22,0.4)',
  },
  errorCard: {
    background: '#450a0a',
    border: '1px solid #ef4444',
    borderRadius: '12px',
    padding: '20px 24px',
    color: '#fca5a5',
    marginTop: '24px',
    fontSize: '14px',
  },
  badge: (color) => ({
    display: 'inline-block',
    background: color + '22',
    color: color,
    border: `1px solid ${color}44`,
    borderRadius: '6px',
    padding: '3px 10px',
    fontSize: '12px',
    fontWeight: '700',
    fontFamily: 'monospace',
  }),
  kvRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '8px',
    fontSize: '13px',
  },
  kvKey: {
    color: '#64748b',
    fontWeight: '600',
    minWidth: '140px',
    flexShrink: 0,
  },
  kvVal: {
    color: '#cbd5e1',
    fontFamily: 'monospace',
  },
};

// ─── Document Renderers ───────────────────────────────────────────────────────

function PODocument({ doc }) {
  if (!doc) return null;
  return (
    <div>
      <div style={{ display: 'flex', gap: '32px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div>
          <div style={S.kvRow}><span style={S.kvKey}>PO Number</span><span style={S.kvVal}>{doc.poNumber}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Retailer</span><span style={S.kvVal}>{doc.retailer}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>EDI ID</span><span style={S.kvVal}>{doc.ediId}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Currency</span><span style={S.kvVal}>{doc.currency}</span></div>
        </div>
        <div>
          <div style={S.kvRow}><span style={S.kvKey}>Ship By</span><span style={S.kvVal}>{doc.shipDate}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Deliver By</span><span style={S.kvVal}>{doc.deliveryDate}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Ship To</span><span style={S.kvVal}>{doc.shipTo?.name}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Location</span><span style={S.kvVal}>{doc.shipTo?.city}, {doc.shipTo?.state} {doc.shipTo?.zip}</span></div>
        </div>
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>#</th>
            <th style={S.th}>SKU</th>
            <th style={S.th}>UPC</th>
            <th style={S.th}>Description</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Qty</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Unit Price</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {(doc.items || []).map((item) => (
            <tr key={item.lineNum}>
              <td style={S.td}>{item.lineNum}</td>
              <td style={S.td}>{item.sku}</td>
              <td style={S.td}>{item.upc}</td>
              <td style={{ ...S.td, fontFamily: 'inherit', color: '#e2e8f0' }}>{item.description}</td>
              <td style={{ ...S.td, textAlign: 'right' }}>{item.qty}</td>
              <td style={{ ...S.td, textAlign: 'right' }}>${item.unitPrice.toFixed(2)}</td>
              <td style={{ ...S.td, textAlign: 'right', color: '#10b981', fontWeight: '700' }}>
                ${(item.qty * item.unitPrice).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={6} style={{ ...S.td, textAlign: 'right', color: '#94a3b8', fontWeight: '700', fontFamily: 'inherit' }}>
              Total
            </td>
            <td style={{ ...S.td, textAlign: 'right', color: '#10b981', fontWeight: '800', fontSize: '15px' }}>
              ${(doc.total || 0).toFixed(2)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function AckDocument({ doc }) {
  if (!doc) return null;
  return (
    <div>
      <div style={S.kvRow}><span style={S.kvKey}>Acknowledged PO</span><span style={S.kvVal}>{doc.acknowledgedPO}</span></div>
      <div style={S.kvRow}><span style={S.kvKey}>Status</span>
        <span style={S.badge('#10b981')}>{doc.status}</span>
      </div>
      <div style={S.kvRow}><span style={S.kvKey}>Retailer</span><span style={S.kvVal}>{doc.retailer}</span></div>
      <div style={S.kvRow}><span style={S.kvKey}>EDI ID</span><span style={S.kvVal}>{doc.ediId}</span></div>
      <div style={S.kvRow}><span style={S.kvKey}>Timestamp</span><span style={S.kvVal}>{doc.timestamp}</span></div>
    </div>
  );
}

function OrderDocument({ doc }) {
  if (!doc) return null;
  return (
    <div>
      <div style={{ display: 'flex', gap: '32px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div>
          <div style={S.kvRow}><span style={S.kvKey}>Order ID</span><span style={S.kvVal}>{doc.orderId}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>PO Number</span><span style={S.kvVal}>{doc.poNumber}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Status</span><span style={S.badge('#f59e0b')}>{doc.status}</span></div>
        </div>
        <div>
          <div style={S.kvRow}><span style={S.kvKey}>Ship By</span><span style={S.kvVal}>{doc.shipDate}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Deliver By</span><span style={S.kvVal}>{doc.deliveryDate}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Ship To</span><span style={S.kvVal}>{doc.shipTo?.name}</span></div>
        </div>
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>SKU</th>
            <th style={S.th}>Description</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Qty</th>
          </tr>
        </thead>
        <tbody>
          {(doc.lineItems || []).map((item) => (
            <tr key={item.lineNum}>
              <td style={S.td}>{item.sku}</td>
              <td style={{ ...S.td, fontFamily: 'inherit', color: '#e2e8f0' }}>{item.description}</td>
              <td style={{ ...S.td, textAlign: 'right' }}>{item.qty}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ASNDocument({ doc }) {
  if (!doc) return null;
  return (
    <div>
      <div style={{ display: 'flex', gap: '32px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div>
          <div style={S.kvRow}><span style={S.kvKey}>PO Number</span><span style={S.kvVal}>{doc.poNumber}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Carrier</span><span style={S.kvVal}>{doc.carrier} ({doc.carrierCode})</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Tracking</span><span style={S.kvVal}>{doc.trackingNumber}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Ship Date</span><span style={S.kvVal}>{doc.shipDate}</span></div>
        </div>
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Carton #</th>
            <th style={S.th}>SSCC</th>
            <th style={S.th}>Weight</th>
            <th style={S.th}>Dimensions</th>
            <th style={S.th}>Contents</th>
          </tr>
        </thead>
        <tbody>
          {(doc.cartons || []).map((carton) => (
            <tr key={carton.cartonId}>
              <td style={S.td}>{carton.cartonId}</td>
              <td style={S.td}>{carton.sscc}</td>
              <td style={S.td}>{carton.weight}</td>
              <td style={S.td}>{carton.dimensions}</td>
              <td style={{ ...S.td, fontFamily: 'inherit', color: '#94a3b8', fontSize: '12px' }}>
                {(carton.items || []).map((i) => `${i.sku} ×${i.qty}`).join(', ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceDocument({ doc }) {
  if (!doc) return null;
  return (
    <div>
      <div style={{ display: 'flex', gap: '32px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div>
          <div style={S.kvRow}><span style={S.kvKey}>Invoice #</span><span style={S.kvVal}>{doc.invoiceNumber}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>PO Number</span><span style={S.kvVal}>{doc.poNumber}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Invoice Date</span><span style={S.kvVal}>{doc.invoiceDate}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Payment Terms</span><span style={S.kvVal}>{doc.paymentTerms}</span></div>
        </div>
        <div>
          <div style={S.kvRow}><span style={S.kvKey}>Ship Date</span><span style={S.kvVal}>{doc.shipDate}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Tracking</span><span style={S.kvVal}>{doc.trackingNumber}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Tax</span><span style={S.kvVal}>${doc.tax}</span></div>
        </div>
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>SKU</th>
            <th style={S.th}>Description</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Qty</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Unit Price</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {(doc.lineItems || []).map((item) => (
            <tr key={item.lineNum}>
              <td style={S.td}>{item.sku}</td>
              <td style={{ ...S.td, fontFamily: 'inherit', color: '#e2e8f0' }}>{item.description}</td>
              <td style={{ ...S.td, textAlign: 'right' }}>{item.qty}</td>
              <td style={{ ...S.td, textAlign: 'right' }}>${item.unitPrice.toFixed(2)}</td>
              <td style={{ ...S.td, textAlign: 'right', color: '#10b981', fontWeight: '700' }}>${item.total}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} style={{ ...S.td, textAlign: 'right', color: '#94a3b8', fontWeight: '700', fontFamily: 'inherit' }}>
              Total
            </td>
            <td style={{ ...S.td, textAlign: 'right', color: '#10b981', fontWeight: '800', fontSize: '16px' }}>
              ${doc.total}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function SummaryDocument({ doc }) {
  if (!doc?.summary) return null;
  const s = doc.summary;
  return (
    <div>
      <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div>
          <div style={S.kvRow}><span style={S.kvKey}>PO Number</span><span style={S.kvVal}>{s.poNumber}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Retailer</span><span style={S.kvVal}>{s.retailer}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Order ID</span><span style={S.kvVal}>{s.orderId}</span></div>
        </div>
        <div>
          <div style={S.kvRow}><span style={S.kvKey}>Tracking</span><span style={S.kvVal}>{s.trackingNumber}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Invoice</span><span style={S.kvVal}>{s.invoiceNumber}</span></div>
          <div style={S.kvRow}><span style={S.kvKey}>Order Total</span><span style={{ ...S.kvVal, color: '#10b981', fontWeight: '700' }}>${s.total}</span></div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {(s.documentsExchanged || []).map((d) => (
          <span key={d} style={S.badge('#3b82f6')}>{d}</span>
        ))}
      </div>
    </div>
  );
}

function renderDoc(stepNum, doc) {
  if (!doc) return null;
  switch (stepNum) {
    case 1: return <PODocument doc={doc} />;
    case 2: return <AckDocument doc={doc} />;
    case 3: return <OrderDocument doc={doc} />;
    case 4: return <ASNDocument doc={doc} />;
    case 5: return <InvoiceDocument doc={doc} />;
    default: return <SummaryDocument doc={doc} />;
  }
}

// ─── Step Card Component ──────────────────────────────────────────────────────

function StepCard({ meta, stepData }) {
  const [expanded, setExpanded] = useState(false);
  const status = stepData?.status || 'pending';
  const isComplete = status === 'complete';
  const isActive = status === 'active';

  // Auto-expand when step completes
  useEffect(() => {
    if (isComplete) setExpanded(true);
  }, [isComplete]);

  const circleContent = isComplete ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ) : isActive ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
      <circle cx="12" cy="12" r="3" fill="white" />
    </svg>
  ) : (
    meta.num
  );

  return (
    <div style={S.stepCard(status, meta.color)}>
      <div style={S.stepHeader} onClick={() => isComplete && setExpanded((v) => !v)}>
        <div style={S.circle(status, meta.color)}>{circleContent}</div>
        <div style={S.stepMeta}>
          <div style={S.stepLabel(status)}>{meta.label} · {meta.subtitle}</div>
          <div style={S.stepTitle(status)}>
            {stepData?.title || meta.subtitle}
          </div>
          {stepData?.description && (
            <div style={S.stepDesc(status)}>{stepData.description}</div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
          {isComplete && stepData?.timing != null && (
            <div style={S.stepTimestamp}>+{(stepData.timing / 1000).toFixed(1)}s</div>
          )}
          {isActive && (
            <div style={{ fontSize: '12px', color: meta.color, fontWeight: '600' }}>
              Processing...
            </div>
          )}
          {isComplete && (
            <div style={{ fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
              {expanded ? '▲ Hide' : '▼ Details'}
            </div>
          )}
        </div>
      </div>
      {isComplete && expanded && stepData?.document && (
        <div style={S.docPanel}>
          {renderDoc(meta.num, stepData.document)}
        </div>
      )}
    </div>
  );
}

// ─── Timer Hook ───────────────────────────────────────────────────────────────

function useTimer(running) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (running) {
      startRef.current = Date.now() - elapsed;
      const tick = () => {
        setElapsed(Date.now() - startRef.current);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } else {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const reset = useCallback(() => {
    setElapsed(0);
    startRef.current = null;
  }, []);

  return { elapsed, reset };
}

// ─── Main Demo Component ──────────────────────────────────────────────────────

export default function Demo({ user, token }) {
  const [retailer, setRetailer] = useState('walmart');
  const [demoState, setDemoState] = useState('idle'); // idle | running | complete | error
  const [steps, setSteps] = useState({});
  const [finalEvent, setFinalEvent] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);

  const esRef = useRef(null);
  const { elapsed, reset: resetTimer } = useTimer(demoState === 'running');

  // Check access: admin user OR ?preview=true
  const params = new URLSearchParams(window.location.search);
  const isPreview = params.get('preview') === 'true';
  const canAccess = isPreview || (user && user.role === 'admin');

  const resetDemo = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    setSteps({});
    setFinalEvent(null);
    setErrorMsg('');
    setDemoState('idle');
    resetTimer();
  }, [resetTimer]);

  const handleRetailerChange = useCallback(
    (e) => {
      setRetailer(e.target.value);
      if (demoState !== 'idle') resetDemo();
    },
    [demoState, resetDemo]
  );

  const runDemo = useCallback(() => {
    resetDemo();

    // Mark all 5 steps as pending
    const initial = {};
    for (let i = 1; i <= 5; i++) initial[i] = { status: 'pending' };
    setSteps(initial);
    setDemoState('running');

    const events = buildDemoEvents(retailer);
    const timers = [];
    events.forEach((payload) => {
      const t = setTimeout(() => {
        if (payload.status === 'done') {
          setFinalEvent(payload);
          setDemoState('complete');
        } else {
          setSteps((prev) => ({ ...prev, [payload.step]: payload }));
        }
      }, payload.timing);
      timers.push(t);
    });
    esRef.current = { close: () => timers.forEach(clearTimeout) };
  }, [retailer, token, resetDemo]);

  const copyShareLink = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('preview', 'true');
    // Point to demo page
    url.pathname = url.pathname.replace(/\/?$/, '');
    const shareUrl = url.toString();
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Fallback
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (esRef.current) esRef.current.close();
    };
  }, []);

  if (!canAccess) {
    return (
      <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '40px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <div style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px' }}>
            Demo Access Required
          </div>
          <div style={{ color: '#94a3b8', marginBottom: '24px' }}>
            This demo is available to admin users or via a shared preview link.
          </div>
          <a href="https://jaychrisedi.com" style={{ color: '#f97316', fontWeight: '600' }}>
            Visit jaychrisedi.com to request a demo
          </a>
        </div>
      </div>
    );
  }

  const timerStr = (elapsed / 1000).toFixed(1) + 's';
  const isRunning = demoState === 'running';
  const isComplete = demoState === 'complete';
  const isError = demoState === 'error';
  const isIdle = demoState === 'idle';

  return (
    <div style={S.page}>
      {/* Keyframe animations injected once */}
      <style>{`
        @keyframes pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(249,115,22,0.5); }
          70% { box-shadow: 0 0 0 10px rgba(249,115,22,0); }
          100% { box-shadow: 0 0 0 0 rgba(249,115,22,0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header */}
      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={S.logo}>JayChris EDI</span>
          <span style={S.logoSub}>— Live Demo</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button style={S.copyBtn} onClick={copyShareLink}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
            </svg>
            {copied ? 'Copied!' : 'Copy share link'}
          </button>
          {(isRunning || isComplete) && (
            <div style={S.timerBox}>{timerStr}</div>
          )}
        </div>
      </div>

      <div style={S.main}>
        {/* Title section */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '36px', fontWeight: '900', letterSpacing: '-1px', marginBottom: '8px', color: 'white' }}>
            Full EDI Cycle Demo
          </h1>
          <p style={{ fontSize: '16px', color: '#94a3b8', maxWidth: '600px' }}>
            Watch a complete PO-to-invoice EDI workflow execute in real time — from the moment a retailer sends a
            purchase order to the final invoice transmission. Zero manual steps.
          </p>
        </div>

        {/* Controls */}
        <div style={S.controlRow}>
          <select
            style={S.select}
            value={retailer}
            onChange={handleRetailerChange}
            disabled={isRunning}
          >
            {RETAILERS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>

          {(isIdle || isError) && (
            <button
              style={S.runBtn}
              onClick={runDemo}
            >
              ▶ Run Demo
            </button>
          )}

          {isRunning && (
            <button style={{ ...S.runBtn, ...S.runBtnDisabled }} disabled>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                style={{ animation: 'spin 1s linear infinite', marginRight: '8px', verticalAlign: 'middle' }}
              >
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
              Running...
            </button>
          )}

          {isComplete && (
            <button style={S.runBtn} onClick={resetDemo}>
              Run Again
            </button>
          )}

          {(isRunning || isComplete) && (
            <button style={S.secondaryBtn} onClick={resetDemo}>
              Reset
            </button>
          )}
        </div>

        {/* Error state */}
        {isError && (
          <div style={S.errorCard}>
            <strong>Demo error:</strong> {errorMsg}
          </div>
        )}

        {/* Step cards */}
        {!isIdle && (
          <div style={S.stepsGrid}>
            {STEP_META.map((meta) => (
              <StepCard
                key={meta.num}
                meta={meta}
                stepData={steps[meta.num]}
              />
            ))}
          </div>
        )}

        {/* Idle placeholder */}
        {isIdle && (
          <div style={{
            border: '1px dashed #334155',
            borderRadius: '16px',
            padding: '60px 40px',
            textAlign: 'center',
            color: '#475569',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.6 }}>⚡</div>
            <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: '#64748b' }}>
              Select a retailer and click "Run Demo"
            </div>
            <div style={{ fontSize: '14px', color: '#475569' }}>
              The full EDI cycle typically completes in under 15 seconds
            </div>
          </div>
        )}

        {/* Completion summary card */}
        {isComplete && finalEvent && (
          <div style={S.summaryCard}>
            <div style={{ fontSize: '14px', fontWeight: '700', letterSpacing: '1px', color: '#6ee7b7', marginBottom: '8px', textTransform: 'uppercase' }}>
              Cycle Complete
            </div>
            <div style={{ fontSize: '32px', fontWeight: '900', color: 'white', marginBottom: '8px', letterSpacing: '-1px' }}>
              Full EDI cycle completed in{' '}
              <span style={{ color: '#10b981' }}>
                {((finalEvent.totalTime || elapsed) / 1000).toFixed(1)}s
              </span>
            </div>
            <div style={{ fontSize: '16px', color: '#6ee7b7', marginBottom: '24px' }}>
              Zero manual intervention required
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
              {['EDI 850 Received', 'EDI 855 Sent', 'Order Created', 'EDI 856 Sent', 'EDI 810 Sent'].map((label) => (
                <span
                  key={label}
                  style={{
                    background: 'rgba(16,185,129,0.2)',
                    border: '1px solid rgba(16,185,129,0.4)',
                    color: '#6ee7b7',
                    borderRadius: '6px',
                    padding: '4px 12px',
                    fontSize: '12px',
                    fontWeight: '700',
                  }}
                >
                  ✓ {label}
                </span>
              ))}
            </div>
            <a
              href="https://jaychrisedi.com"
              target="_blank"
              rel="noopener noreferrer"
              style={S.ctaBtn}
            >
              Start Your Free Trial
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
